import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import OrnamentalDivider from "@/components/OrnamentalDivider";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) { setError("Missing session_id"); setLoading(false); return; }
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooking(data.booking);
          setLoading(false);
          return;
        }
        if (attempts > 10) {
          setError("Payment is still processing. Check your email in a few minutes.");
          setLoading(false);
          return;
        }
        setTimeout(tick, 1500);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not verify payment");
        setLoading(false);
      }
    };
    tick();
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success">
      {loading && (
        <>
          <Loader2 size={48} className="mx-auto text-[#6E2638] animate-spin" />
          <div className="serif text-3xl text-[#6E2638] mt-6">Confirming your payment…</div>
        </>
      )}
      {!loading && booking && (
        <>
          <CheckCircle2 size={56} className="mx-auto text-[#1E4637]" />
          <div className="serif text-5xl text-[#6E2638] mt-6">Paid — you're on the walk.</div>
          <OrnamentalDivider label="Booking confirmed" />
          <div className="card-warm rounded-2xl p-8 text-left">
            <div className="text-sm text-[#1A1817]/70">Reference</div>
            <div className="serif text-xl text-[#6E2638] break-all">{booking.id}</div>
            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
              <div><div className="text-[#1A1817]/60">Guest</div><div className="serif text-lg text-[#6E2638]">{booking.full_name}</div></div>
              <div><div className="text-[#1A1817]/60">Guests</div><div className="serif text-lg text-[#6E2638]">{booking.guests}</div></div>
              <div><div className="text-[#1A1817]/60">Date</div><div className="serif text-lg text-[#6E2638]">{booking.tour_date}</div></div>
              <div><div className="text-[#1A1817]/60">Slot</div><div className="serif text-lg text-[#6E2638]">{booking.time_slot}</div></div>
            </div>
            <hr className="my-6 border-[#E5D9C5]" />
            <div className="flex justify-between items-baseline">
              <div className="text-[#1A1817]/70">Total paid</div>
              <div className="serif text-3xl text-[#6E2638]">AUD ${booking.total_price_aud.toFixed(2)}</div>
            </div>
          </div>
          <p className="text-sm text-[#1A1817]/70 mt-6">
            A confirmation email has been sent. Meeting point: Federation Square — in front of the big screen.
          </p>
          <Link to="/" className="btn-outline-gold mt-8 inline-flex" data-testid="success-home">
            Back to home
          </Link>
        </>
      )}
      {!loading && error && (
        <>
          <div className="serif text-3xl text-[#6E2638]">Payment status</div>
          <p className="text-[#1A1817]/80 mt-4">{error}</p>
          <Link to="/individual" className="btn-primary mt-8 inline-flex">Back to booking</Link>
        </>
      )}
    </div>
  );
}
